import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "./Navbar";
import { Container, Card, Table, Alert, Spinner, Row, Col, Form, Button, Dropdown, Modal, Pagination, FloatingLabel } from "react-bootstrap";
import { API_ENDPOINTS } from './config/api';
import ProgramPGYUtils from './utils/programPGYUtils';
import PageTitle from './components/PageTitle';

const DirectorResidentLogView = () => {
  const { id } = useParams();
  const [logs, setLogs] = useState([]);
  const [residentName, setResidentName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    startDate: '',
    endDate: '',
    pgyLevel: '',
    rotation: '',
    attending: '',
    reportType: 'detailed'
  });
  const [rotations, setRotations] = useState([]);
  const [attendings, setAttendings] = useState([]);
  
  // PGY level configuration
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);

  // Fetch program details and PGY levels
  const fetchProgramDetails = async () => {
    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_ENDPOINTS.DIRECTOR}/resident-case-logs/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch case logs');
        }
        return res.json();
      })
      .then(data => {
        if (data && data.length > 0) {
          setLogs(data);
          setResidentName(data[0].residentName || "Unknown Resident");
          
          // Extract unique rotations and attendings
          const uniqueRotations = [...new Set(data.map(log => log.rotation))].filter(Boolean).sort();
          const uniqueAttendings = [...new Set(data.map(log => log.attending))].filter(Boolean).sort();
          setRotations(uniqueRotations);
          setAttendings(uniqueAttendings);
        }
      })
      .catch(err => {
        setError(err.message);
        console.error('Error fetching case logs:', err);
      })
      .finally(() => setLoading(false));
      
    // Also fetch program details and PGY levels
    fetchProgramDetails();
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(logs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = logs.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Export functionality
  const handleExport = () => {
    const headers = [
      'Procedure Name',
      'Date',
      'Rotation',
      'Attending',
      'Patient Population',
      'Role',
      'PGY Level',
      'Robotic',
      'Minimally Invasive'
    ];
    
    const csvContent = [
      headers.join(','),
      ...logs.map(log => [
        `"${log.procedureName}"`,
        new Date(log.date).toLocaleDateString(),
        `"${log.rotation}"`,
        `"${log.attending}"`,
        `"${log.patientPopulation}"`,
        `"${log.role}"`,
        log.pgyLevel,
        log.robotic ? 'Yes' : 'No',
        log.minimallyInvasive ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `case_logs_${residentName.replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateReport = () => {
    // Filter logs based on export filters
    let filteredData = logs.filter(log => {
      // Date range filter
      if (exportFilters.startDate && exportFilters.endDate) {
        const logDate = new Date(log.date);
        const startDate = new Date(exportFilters.startDate);
        const endDate = new Date(exportFilters.endDate);
        if (logDate < startDate || logDate > endDate) return false;
      }
      
      // PGY level filter
      if (exportFilters.pgyLevel && log.pgyLevel !== parseInt(exportFilters.pgyLevel)) {
        return false;
      }
      
      // Rotation filter
      if (exportFilters.rotation && log.rotation !== exportFilters.rotation) {
        return false;
      }
      
      // Attending filter
      if (exportFilters.attending && log.attending !== exportFilters.attending) {
        return false;
      }
      
      return true;
    });

    // Generate report based on type
    let reportData;
    let reportTitle;
    
    switch (exportFilters.reportType) {
      case 'detailed':
        reportData = generateDetailedReport(filteredData);
        reportTitle = `Detailed Case Log Report - ${residentName}`;
        break;
      case 'summary':
        reportData = generateSummaryReport(filteredData);
        reportTitle = `Summary Case Log Report - ${residentName}`;
        break;
      case 'statistics':
        reportData = generateStatisticsReport(filteredData);
        reportTitle = `Statistics Case Log Report - ${residentName}`;
        break;
      default:
        reportData = generateDetailedReport(filteredData);
        reportTitle = `Case Log Report - ${residentName}`;
    }

    // Create HTML report for display and printing
    const htmlReport = generateHTMLReport(reportData, reportTitle, exportFilters, filteredData.length);
    
    // Open in new tab
    const newWindow = window.open('', '_blank');
    newWindow.document.write(htmlReport);
    newWindow.document.close();
    
    setShowExportModal(false);
  };

  const generateDetailedReport = (data) => {
    const headers = [
      'Procedure Name',
      'Date',
      'Rotation',
      'Attending',
      'Patient Population',
      'Role',
      'PGY Level',
      'Robotic',
      'Minimally Invasive'
    ];
    
    const csvContent = [
      headers.join(','),
      ...data.map(log => [
        `"${log.procedureName || ''}"`,
        new Date(log.date).toLocaleDateString(),
        `"${log.rotation || ''}"`,
        `"${log.attending || ''}"`,
        `"${log.patientPopulation || ''}"`,
        `"${log.role || ''}"`,
        log.pgyLevel || '',
        log.robotic ? 'Yes' : 'No',
        log.minimallyInvasive ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    return csvContent;
  };

  const generateSummaryReport = (data) => {
    // Group by rotation and count procedures
    const summaryByRotation = data.reduce((acc, log) => {
      const rotation = log.rotation || 'Unknown';
      if (!acc[rotation]) {
        acc[rotation] = {
          totalCases: 0,
          roboticCases: 0,
          minimallyInvasiveCases: 0,
          procedures: {}
        };
      }
      acc[rotation].totalCases++;
      if (log.robotic) acc[rotation].roboticCases++;
      if (log.minimallyInvasive) acc[rotation].minimallyInvasiveCases++;
      
      const procedure = log.procedureName || 'Unknown';
      acc[rotation].procedures[procedure] = (acc[rotation].procedures[procedure] || 0) + 1;
      
      return acc;
    }, {});

    const headers = ['Rotation', 'Total Cases', 'Robotic Cases', 'Minimally Invasive Cases', 'Top Procedures'];
    
    const csvContent = [
      headers.join(','),
      ...Object.entries(summaryByRotation).map(([rotation, stats]) => {
        const topProcedures = Object.entries(stats.procedures)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 3)
          .map(([proc, count]) => `${proc} (${count})`)
          .join('; ');
        
        return [
          `"${rotation}"`,
          stats.totalCases,
          stats.roboticCases,
          stats.minimallyInvasiveCases,
          `"${topProcedures}"`
        ].join(',');
      })
    ].join('\n');

    return csvContent;
  };

  const generateStatisticsReport = (data) => {
    const totalCases = data.length;
    const roboticCases = data.filter(log => log.robotic).length;
    const minimallyInvasiveCases = data.filter(log => log.minimallyInvasive).length;
    
    // Group by role
    const roleStats = data.reduce((acc, log) => {
      const role = log.role || 'Unknown';
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {});

    // Group by patient population
    const populationStats = data.reduce((acc, log) => {
      const population = log.patientPopulation || 'Unknown';
      acc[population] = (acc[population] || 0) + 1;
      return acc;
    }, {});

    const headers = ['Metric', 'Count', 'Percentage'];
    const rows = [
      ['Total Cases', totalCases, '100%'],
      ['Robotic Cases', roboticCases, `${((roboticCases / totalCases) * 100).toFixed(1)}%`],
      ['Minimally Invasive Cases', minimallyInvasiveCases, `${((minimallyInvasiveCases / totalCases) * 100).toFixed(1)}%`],
      ...Object.entries(roleStats).map(([role, count]) => [
        `${role} Role`, count, `${((count / totalCases) * 100).toFixed(1)}%`
      ]),
      ...Object.entries(populationStats).map(([population, count]) => [
        `${population} Population`, count, `${((count / totalCases) * 100).toFixed(1)}%`
      ])
    ];

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  };

  const generateHTMLReport = (reportData, reportTitle, exportFilters, totalCases) => {
    const currentDate = new Date().toLocaleDateString();
    const filtersApplied = [];
    
    if (exportFilters.startDate && exportFilters.endDate) {
      filtersApplied.push(`Date Range: ${exportFilters.startDate} to ${exportFilters.endDate}`);
    }
    if (exportFilters.pgyLevel) {
      filtersApplied.push(`PGY Level: ${exportFilters.pgyLevel}`);
    }
    if (exportFilters.rotation) {
      filtersApplied.push(`Rotation: ${exportFilters.rotation}`);
    }
    if (exportFilters.attending) {
      filtersApplied.push(`Attending: ${exportFilters.attending}`);
    }

    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${reportTitle}</title>
        <style>
          @media print {
            .no-print { display: none !important; }
            body { margin: 0; padding: 20px; }
            .page-break { page-break-before: always; }
            .report-container { 
              box-shadow: none !important; 
              padding: 0 !important; 
              margin: 0 !important; 
              max-width: none !important;
              background: white !important;
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
          }
          
          .report-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            padding: 30px;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            border-radius: 8px;
          }
          
          .report-header {
            text-align: center;
            border-bottom: 3px solid #D3D6DA;
            padding-bottom: 20px;
            margin-bottom: 30px;
            position: relative;
          }
          
          .brand-logo {
            position: absolute;
            top: 0;
            left: 0;
            text-align: left;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .logo-image {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .brand-text {
            display: flex;
            flex-direction: column;
          }
          
          .brand-name {
            font-size: 18px;
            font-weight: bold;
          }
          
          .brand-tagline {
            font-size: 12px;
            color: #666;
            margin: 0;
          }
          
          .report-title {
            font-size: 28px;
            font-weight: bold;
            color: #333;
            margin: 0 0 10px 0;
          }
          
          .report-subtitle {
            font-size: 16px;
            color: #666;
            margin: 0;
          }
          
          .report-meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 5px;
          }
          
          .meta-section {
            flex: 1;
          }
          
          .meta-title {
            font-weight: bold;
            color: #333;
            margin-bottom: 5px;
          }
          
          .meta-content {
            color: #666;
          }
          
          .filters-applied {
            margin-bottom: 20px;
            padding: 15px;
            background-color: #e3f2fd;
            border-left: 4px solid #2196f3;
            border-radius: 4px;
          }
          
          .filters-title {
            font-weight: bold;
            color: #1976d2;
            margin-bottom: 10px;
          }
          
          .filter-item {
            margin: 5px 0;
            color: #333;
          }
          
          .print-buttons {
            text-align: center;
            margin: 30px 0;
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 8px;
          }
          
          .btn {
            padding: 12px 24px;
            margin: 0 10px;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 16px;
            text-decoration: none;
            display: inline-block;
          }
          
          .btn-primary {
            background-color: #007bff;
            color: white;
          }
          
          .btn-secondary {
            background-color: #6c757d;
            color: white;
          }
          
          .btn:hover {
            opacity: 0.8;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 14px;
          }
          
          th, td {
            border: 1px solid #D3D6DA !important;
            padding: 12px;
            text-align: left;
          }
          
          th:nth-child(n+2), td:nth-child(n+2) {
            text-align: center;
          }
          
          th {
            background-color: #f8f9fa;
            font-weight: bold;
          }
          
          tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          
          tr:hover {
            background-color: #e3f2fd;
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <div class="report-header">
            <div class="brand-logo">
              <div class="logo-image me-2">
                <span><img src="/images/logo3.svg" alt="Medhive Logo" style="width: 48px; height: 48px;"/></span>
              </div>
              <div class="brand-text">
                <div class="brand-name">Medhive</div>
                <div class="brand-tagline">Resident Management System</div>
              </div>
            </div>
            <h1 class="report-title">${reportTitle}</h1>
            <p class="report-subtitle">Generated on ${currentDate}</p>
            <p class="report-subtitle">For ${residentName}</p>
          </div>

          <div class="report-meta">
            <div class="meta-section">
              <div class="meta-title">Total Cases</div>
              <div class="meta-content">${totalCases}</div>
            </div>
            <div class="meta-section">
              <div class="meta-title">Report Type</div>
              <div class="meta-content">${reportTitle}</div>
            </div>
            <div class="meta-section">
              <div class="meta-title">Generated By</div>
              <div class="meta-content">Medhive Case Log System</div>
            </div>
          </div>
          
          ${filtersApplied.length > 0 ? `
            <div class="filters-applied">
              <div class="filters-title">Filters Applied:</div>
              ${filtersApplied.map(filter => `<div class="filter-item">• ${filter}</div>`).join('')}
            </div>
          ` : ''}
          
          <div class="print-buttons no-print">
            <button class="btn btn-primary" onclick="window.print()">Print Report</button>
            <button class="btn btn-secondary" onclick="downloadCSV()">Download CSV</button>
            <button class="btn btn-secondary" onclick="window.close()">Close</button>
          </div>
          
          <div class="report-content">
            <table>
              <thead>
                <tr>
                  ${reportData.split('\n')[0].split(',').map(header => `<th>${header.replace(/"/g, '')}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${reportData.split('\n').slice(1).filter(line => line.trim()).map(line => `
                  <tr>
                    ${line.split(',').map(cell => `<td>${cell.replace(/"/g, '')}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
        
        <script>
          function downloadCSV() {
            const csvContent = \`${reportData.replace(/`/g, '\\`')}\`;
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', '${reportTitle.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        </script>
      </body>
      </html>
    `;
    
    return html;
  };

  return (
    <div className="bg-light min-vh-100">
      <PageTitle title="Resident Case Logs" description="View and manage individual resident case logs" />
      <Navbar pageTitle="Resident Case Logs" />
      <Container className="py-4">
        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="border-bottom p-4" style={{ background: 'var(--bs-card-bg)' }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {residentName && (
                  <p className="mb-0">
                    Resident: <span className="fw-bold">{residentName}</span>
                  </p>
                )}
              </div>
              <div className="d-flex gap-2">
                <Dropdown>
                  <Dropdown.Toggle variant="outline-primary" size="sm" id="export-dropdown">
                    Export
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={handleExport}>
                      Export to Excel
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowExportModal(true)}>
                      Generate Report
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {loading && (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}

            {error && (
              <Alert variant="danger" className="m-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}
            
            {!loading && !error && (
              <Table responsive hover className="mb-0">
                <thead>
                  <tr className="custom-header-bg">
                    <th className="px-4">Procedure</th>
                    <th>Date</th>
                    <th>PGY Level</th>
                    <th>Rotation</th>
                    <th>Attending</th>
                    <th>Population</th>
                    <th>Role</th>
                    <th>Robotic</th>
                    <th>Minimally Invasive</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map((log) => (
                    <tr key={log.id}>
                      <td className="px-4">{log.procedureName}</td>
                      <td>{formatDate(log.date)}</td>
                      <td>{log.pgyLevel}</td>
                      <td>{log.rotation}</td>
                      <td>{log.attending}</td>
                      <td>{log.patientPopulation}</td>
                      <td>{log.role}</td>
                      <td>{log.robotic ? "Yes" : "No"}</td>
                      <td>{log.minimallyInvasive ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Enhanced Pagination */}
        {logs.length > 0 && (
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <span className="text-muted">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, logs.length)} of {logs.length} case logs
                  </span>
                  <Form.Select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                    style={{ width: 'auto' }}
                    size="sm"
                    c
                  >
                    <option value={5}>5/page</option>
                    <option value={10}>10/page</option>
                    <option value={25}>25/page</option>
                    <option value={50}>50/page</option>
                  </Form.Select>
                </div>
                
                {totalPages > 1 && (
                  <Pagination size="sm">
                    <Pagination.First 
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    
                    {/* Show page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }
                      
                      return (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
      </div>
            </Col>
          </Row>
        )}

        {/* Export Report Modal */}
        <Modal show={showExportModal} onHide={() => setShowExportModal(false)} size="md">
          <Modal.Header closeButton className="border-bottom-0 px-4">
            <Modal.Title> <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Generate Case Log Report</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4">
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Start Date" className="mb-3">
                    <Form.Control
                      type="date"
                      value={exportFilters.startDate}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="End Date" className="mb-3">
                    <Form.Control
                      type="date"
                      value={exportFilters.endDate}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="PGY Level" className="mb-3">
                    <Form.Select
                      value={exportFilters.pgyLevel}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, pgyLevel: e.target.value }))}
                    >
                      <option value="">All</option>
                      {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Rotation" className="mb-3">
                    <Form.Select
                      value={exportFilters.rotation}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, rotation: e.target.value }))}
                    >
                      <option value="">All Rotations</option>
                      {rotations.map(rotation => (
                        <option key={rotation} value={rotation}>{rotation}</option>
                      ))}
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Attending" className="mb-3">
                    <Form.Select
                      value={exportFilters.attending}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, attending: e.target.value }))}
                    >
                      <option value="">All Attendings</option>
                      {attendings.map(attending => (
                        <option key={attending} value={attending}>{attending}</option>
                      ))}
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Report Type" className="mb-3">
                    <Form.Select
                      value={exportFilters.reportType}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, reportType: e.target.value }))}
                    >
                      <option value="detailed">Detailed Report</option>
                      <option value="summary">Summary Report</option>
                      <option value="statistics">Statistics Report</option>
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-top-0 px-4">
            <Button variant="light" size="sm" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleGenerateReport}>
              Generate Report
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default DirectorResidentLogView;
